<template>
  <div class="mt-3">
    <ValidationObserver ref="formObserver">
      <!-- Client -->
      <div class="mt-1">
        <SubHeaderSection
          title="Cliente"
          description="Buscar y seleccionar el cliente"
          bg-icon="#1d58b7"
          bg-color="#f0f0f0"
        >
          <font-awesome-icon :icon="['fas', 'industry-alt']" size="lg" fixed-width />
        </SubHeaderSection>

        <div class="sc-sub-body">
          <div v-if="fetchingClient">
            <b-alert show variant="info" class="fetching-client-banner">
              <b-spinner small type="grow"></b-spinner>
              <span class="fs-16">Obteniendo información del cliente...</span>
            </b-alert>
          </div>
          <!-- Sin cliente registrado. -->
          <div v-else-if="continueWithoutClient">
            <b-alert show variant="warning" class="no-client-banner">
              <font-awesome-icon :icon="['far', 'info-circle']" fixed-width  />
              <span>Continuando sin cliente registrado</span>
              <button type="button" class="no-client-undo" @click="resetClient">Cambiar</button>
            </b-alert>
          </div>

          <ClientSearch
            v-show="!continueWithoutClient && !fetchingClient"
            :clientOptional="true"
            :clientSelected="client"
            :disable-input="fetchingClient"
            @select-client="handleSelectClient"
            @reset-client="resetClient"
            @set-no-client="setNoClient"
          />

          <div v-if="!form.client.id">
            <ValidationProvider :rules="{required: true, max: 100, min: 5}" name="nombre" v-slot="{ valid, errors }">
              <b-form-group class="mt-2 mb-0">
                <template #label>
                  <span>Nombre del cliente</span><span class="asterisk">*</span>
                </template>
                <b-input
                  v-model="form.client.name"
                  type="text"
                  id="client-name"
                  placeholder="Nombre del cliente"
                  :state="(errors[0] || !valid)  ? false : true"
                  @blur="onBlurClientName"
                />
              </b-form-group>
            </ValidationProvider>
          </div>
        </div>
      </div>

      <!-- Contact -->
      <div>
        <SubHeaderSection
          title="Contacto"
          description="Información de contacto"
          bg-icon="#1d58b7"
          bg-color="#f0f0f0"
        >
          <font-awesome-icon :icon="['far', 'address-card']" size="xl" fixed-width />
        </SubHeaderSection>

        <div class="sc-sub-body">
          <b-form-group v-if="form.client.id" class="mb-1">
            <template #label>
              <span>Seleccionar contacto</span>
              <span class="text-muted fs-12">(opcional)</span>
            </template>
            <b-select v-model="selectedContactId" :options="contactOptions" @change="onContactSelect">
              <template v-slot:first>
                <option :value="null">-- Seleccionar --</option>
              </template>
            </b-select>
          </b-form-group>

          <b-row>
            <b-col md="12">
              <ValidationProvider :rules="{required: true, max: 100}" name="nombre" v-slot="{ valid, errors }">
                <b-form-group class="mt-2 mb-0">
                  <template #label>
                    <span>Solicitado por</span><span class="asterisk">*</span>
                  </template>
                  <b-input
                    v-model="form.contact.solicited_by"
                    type="text"
                    id="contact-name"
                    :state="(errors[0] || !valid)  ? false : true"
                    :disabled="!!form.contact.id"
                    placeholder="Contacto | Persona que solicita"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>
            <b-col md="6">
              <ValidationProvider :rules="{required: true, max: 20}" name="teléfono" v-slot="{ valid, errors }">
                <b-form-group class="mt-2 mb-0">
                  <template #label>
                    <span>Teléfono</span><span class="asterisk">*</span>
                  </template>
                  <b-input
                    v-model="form.contact.telephone"
                    type="text"
                    id="contact-phone"
                    placeholder="8888888"
                    :state="(errors[0] || !valid)  ? false : true"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>
            <b-col md="6">
              <ValidationProvider :rules="{required: false, email: true}" name="correo" v-slot="{ valid, errors }">
                <b-form-group class="mt-2 mb-0">
                  <template #label>
                    <span>Correo electrónico</span>
                  </template>
                  <b-input
                    v-model="form.contact.contact_email"
                    type="text"
                    id="contact-email"
                    placeholder="correo@empresa.com"
                    :state="(errors[0] || !valid)  ? false : true"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Address -->
      <div class="mt-2">
        <SubHeaderSection
          title="Dirección"
          description="Ubicación donde se require el servicio"
          bg-icon="#1d58b7"
          bg-color="#f0f0f0"
        >
          <font-awesome-icon :icon="['fas', 'map-marker-alt']" size="xl" fixed-width />
        </SubHeaderSection>

        <div class="sc-sub-body">
          <b-form-group v-if="form.client.id" class="mb-1">
            <template #label>
              <span>Seleccionar dirección</span>
              <span class="text-muted fs-12">(opcional)</span>
            </template>
            <b-select v-model="selectedAddressId" :options="addressOptions" @change="onAddressSelect">
              <template v-slot:first>
                <option :value="null">-- Seleccionar --</option>
              </template>
            </b-select>
          </b-form-group>

          <b-row>
            <b-col>
              <ValidationProvider :rules="{required: true}" name="Dirección" v-slot="{ valid, errors }">
                <b-form-group class="mb-0">
                  <template #label>
                    <span>Tipo de asistencia</span><span class="asterisk">*</span>
                  </template>
                  <b-select
                    v-model="form.address.assistance_type"
                    :options="locationTypeOptions"
                    :state="(errors[0] || !valid)  ? false : true"
                    @change="onChangeAssistanceType"
                  >
                    <template v-slot:first>
                      <option :value="null" disabled>-- Seleccionar --</option>
                    </template>
                  </b-select>
                </b-form-group>
              </ValidationProvider>
            </b-col>
          </b-row>

          <b-row v-if="form.address.assistance_type == 'En sitio'" class="mt-2">
            <b-col>
              <b-form-group class="mb-0">
                <template #label>
                  <span>Coordenadas geográficas</span>
                  <span class="text-muted fs-12">(opcional)</span>
                </template>
                <b-input-group>
                  <b-input
                    v-model="form.address.geo_location"
                    id="address-geo_location"
                    type="text"
                    placeholder="9.9281, -84.0907"
                    :state="coordinates.isValid ? null : false"
                    @blur="onGeoBlur"
                  />
                  <template #append>
                    <b-form-checkbox
                      id="checkbox-1"
                      v-model="showMap"
                      size="lg"
                      name="checkbox-showMap"
                      :value="true"
                      class="ml-2 align-self-center"
                      :unchecked-value="false"
                    >
                      <span class="fs-18">Fijar en mapa</span>
                    </b-form-checkbox>
                  </template>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-show="form.address.assistance_type == 'En sitio' && showMap" class="mt-1">
            <b-col>
              <GoogleMaps
                :coordinates="coordinates"
                @update-coordinates="onMarkerMove"
              />
            </b-col>
          </b-row>

          <b-row v-if="form.address.assistance_type == 'En sitio'" class="mt-1">
            <b-col md="12">
              <ValidationProvider :rules="{required: true, max: 240}" name="Dirección" v-slot="{ valid, errors }">
                <b-form-group>
                  <template #label>
                    <span>Dirección</span><span class="asterisk">*</span>
                  </template>
                  <b-textarea
                    placeholder="Ej: Sabana sur 50 este del colegio, 30 mtrs oeste"
                    v-model="form.address.address"
                    rows="3"
                    :state="(errors[0] || !valid)  ? false : true"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>
          </b-row>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import SubHeaderSection from '../SubHeaderSection'
import ClientSearch from '../../../shared/ClientSearchV2'
import { ValidationObserver, ValidationProvider } from "vee-validate"
import GoogleMaps from '../../../shared/GoogleMaps'
import { locationTypeOptions } from '../../../../sharedOptions/serviceCallModule'

export default {
  name: 'FirstStep',
  components: {
    SubHeaderSection, ClientSearch, ValidationObserver, ValidationProvider, GoogleMaps
  },
  props: {
    form: { type: Object, required: true },
  },
  data() {
    return {
      continueWithoutClient: false,
      addressSearch: '',
      showAddressDropdown: false,
      locationTypeOptions: locationTypeOptions,
      // contacts:
      clientContacts: [],
      selectedContactId: null,
      // addresses:
      selectedAddressId: null,
      clientAddresses: [],
      client: {},
      fetchingClient: false,
      showMap: false,
      coordinates: {
        lat: 9.946588,
        lng: -84.075483,
        isValid: true
      },
      geoTimeout: null
    }
  },
  mounted() {
    this.getClientByRoute()
  },
  computed: {
    contactOptions () {
      return this.clientContacts.map(contact => ({ text: contact.name, value: contact.id }))
    },
    addressOptions () {
      return this.clientAddresses.map(address => ({ text: address.address, value: address.id }))
    }
  },
  methods: {
    // client
    getClientByRoute () {
      let client_id = this.$route.query.client
      if (!client_id || isNaN(client_id)) return
      client_id = Number(client_id)

      if (!client_id) return
      this.fetchingClient = true
      this.$http.get(`/api/production/clients/${client_id}/fetch_related_data`)
        .then(response => {
          // console.log(response);
          const { client_addresses, client_contacts } = response.data

          this.client = {
            id: response.data.id,
            client_num: response.data.client_num,
            name: response.data.name,
            payment_terms: response.data.payment_terms,
            sap_frozen: response.data.sap_frozen,
            sap_valid: response.data.sap_valid
          }

          this.form.client.id = this.client.id
          this.form.client.client_num = this.client.client_num
          this.form.client.name = this.client.name
          this.form.client.payment_terms = this.client.payment_terms
          this.continueWithoutClient = false

          this.resetSelectedContact()
          this.resetSelectedAddress()

          this.clientContacts = client_contacts
          this.clientAddresses = client_addresses
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          setTimeout(() => {
            this.fetchingClient = false
          }, 1000)
        })
    },
    onBlurClientName () {
      if (!this.form.client.name || this.form.client.name.length < 5) return
      if (this.continueWithoutClient) return

      this.continueWithoutClient = true
    },
    // forms
    async checkFormValidity () {
      return await this.$refs.formObserver.validate()
    },
    // Client section
    handleSelectClient (clientSelected) {
      this.form.client.id = clientSelected.id
      this.form.client.client_num = clientSelected.client_num
      this.form.client.name = clientSelected.name
      this.form.client.payment_terms = clientSelected.payment_terms
      this.continueWithoutClient = false
      this.resetSelectedContact()
      this.resetSelectedAddress()
      this.getClientRelatedInfo(this.form.client.id)
    },
    resetClient () {
      this.form.client.id = null
      this.form.client.client_num = null
      this.form.client.name = null
      this.form.client.payment_terms = null
      this.continueWithoutClient = false
      this.resetSelectedContact()
      this.resetSelectedAddress()
    },
    setNoClient () {
      this.resetClient()
      this.continueWithoutClient = true
    },
    getClientRelatedInfo (client_id) {
      if (!client_id) return
      this.$http.get(`/api/production/clients/${client_id}/fetch_related_data`)
        .then(response => {
          // console.log(response);
          const { client_addresses, client_contacts } = response.data
          this.clientContacts = client_contacts
          this.clientAddresses = client_addresses
        })
        .catch(error => {
          console.log(error)
        })
    },
    // Contact section
    onContactSelect (id) {
      const contact = this.clientContacts.find(x => x.id === id) || {}

      this.form.contact.id = contact.id || null
      this.form.contact.solicited_by = contact.name || ''
      this.form.contact.telephone = contact.phone || ''
      this.form.contact.contact_email = contact.email || ''
    },
    resetSelectedContact () {
      this.form.contact.id = null
      this.form.contact.solicited_by = ''
      this.form.contact.telephone = ''
      this.form.contact.contact_email = ''
      this.selectedContactId = null
      this.clientContacts = []
    },
    // Address section
    onAddressSelect (a) {
      const address = this.clientAddresses.find(x => x.id === a) || {}

      this.form.address.id = address.id || null
      this.form.address.address = address.street || ''
      this.form.address.geo_location = address.geo_location || ''
    },
    resetSelectedAddress () {
      this.form.address.id = null
      this.form.address.address = ''
      this.form.address.geo_location = ''
      this.form.address.assistance_type = 'En sitio'
      this.selectedAddressId = null
      this.clientAddresses = []
    },
    onChangeAssistanceType () {
      this.selectedAddressId = null
      this.form.address.address = null
      this.form.address.geo_location = null
    },
    onGeoBlur () {
      clearTimeout(this.geoTimeout)
      if (!this.showMap) return

      this.geoTimeout = setTimeout(() => {
        const value = (this.form.address.geo_location || '').trim()

        const regex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/
        if (!regex.test(value)) {
          this.coordinates.isValid = false
          return
        }

        const [lat, lng] = value.split(',')
        const coords = {
          lat: Number(lat),
          lng: Number(lng)
        }

        this.setCoordinates(coords)
      }, 400)
    },
    onMarkerMove (coordinates) {
      this.setCoordinates(coordinates)

      this.form.address.geo_location =
        `${this.coordinates.lat}, ${this.coordinates.lng}`
    },
    setCoordinates (coordinates) {
      if (this.coordinates.lat === coordinates.lat && this.coordinates.lng === coordinates.lng) return

      this.coordinates = {
        lat: Number(coordinates.lat),
        lng: Number(coordinates.lng),
        isValid: true
      }
    }
  }
}
</script>

<style scoped>
  .sc-sub-body { padding: 0px 24px 20px; }

  .no-client-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 0.7rem;
    color: #92400e;
    margin-bottom: 0px;
    min-height: 32px;
  }
  .fetching-client-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 0.7rem;
    margin-bottom: 0px;
    min-height: 57px;
  }
  .no-client-undo { margin-left: auto; background: none; border: none; color: #e8380d; font-size: 0.78rem; font-weight: 600; cursor: pointer; text-decoration: underline; padding: 0; }

  @media (max-width: 640px) {
    .sc-sub-body { padding-left: 16px; padding-right: 16px; }
  }
</style>
